<script>
import getErrorMessage from '@/modules/server-errors';
import { basename, extname, replaceFilename, safeFilename } from '@/modules/file-utilities';

export default {
  name: 'DuplicateDialog',
  props: {
    site: Object
  },
  data() {
    return {
      dialogVisible: false,
      duplicating: false,
      form: {
        name: '',
        filename: '',
        extension: ''
      },
      invalid: [],
      page: {}
    };
  },
  methods: {
    // Prevents unsafe characters from being entered into filename inputs
    handleFilenameChange() {
      this.form.filename = safeFilename(this.form.filename);
    },

    // Generate a filename basded on the name that was entered
    handleNameInput() {
      this.form.filename = safeFilename(this.form.name);
    },

    // Opens the dialog
    open(page) {
      this.page = Object.assign({}, page);
      this.form.name = this.page.name;
      this.form.extension = extname(this.page.path);
      this.form.filename = basename(this.page.path, this.form.extension);
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.name.select());
    },

    // Closes the dialog
    close() {
      this.dialogVisible = false;
    },

    // Handles for submission
    submit() {
      const name = this.form.name;
      const filename = safeFilename(this.form.filename);
      const pageId = this.page.id;
      const path = replaceFilename(this.page.path, filename);

      // Check for invalid fields
      this.invalid = [];
      if (!name) this.invalid.push('name');
      if (!filename) this.invalid.push('filename');
      if (this.invalid.length) {
        this.$notify({
          type: 'error',
          title: this.$t('Duplicate'),
          message: this.$t('Please correct the highlighted errors.')
        });
        return;
      }

      // Duplicate the page
      this.duplicating = true;
      this.$http
        .post(`/sites/${this.site.id}/pages`, { name, path, pageId })
        .then(res => {
          this.$emit('complete', res.data.page);
          this.close();
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Duplicate'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.duplicating = false));
    }
  }
};
</script>

<template>
  <el-dialog
    :title="$t('Duplicate')"
    :visible.sync="dialogVisible"
  >
    <el-form @submit.native.prevent="submit">
      <!-- Name -->
      <el-form-item
        :class="{ 'is-error': invalid.includes('name') }"
        :label="$t('Name')"
      >
        <el-input
          ref="name"
          v-model="form.name"
          type="text"
          prefix-icon="el-icon-fa-tag"
          auto-complete="off"
          @input.native="handleNameInput"
        />
      </el-form-item>

      <!-- Filename -->
      <el-form-item
        :class="{ 'is-error': invalid.includes('filename') }"
        :label="$t('Filename')"
      >
        <el-input
          v-model="form.filename"
          type="text"
          auto-complete="off"
          prefix-icon="el-icon-fa-file"
          @change="handleFilenameChange"
        >
          <template slot="append">
            {{ form.extension }}
          </template>
        </el-input>
      </el-form-item>

      <!-- Allow the form to submit when enter is pressed inside a field -->
      <el-button v-show="false" native-type="submit" />
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="close">
        {{ $t('Cancel') }}
      </el-button>
      <el-button :loading="duplicating" type="primary" @click="submit">
        {{ $t('Duplicate') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet
</style>
